import React, { useEffect, useMemo, useRef, useState } from "react"
import PropTypes from "prop-types"
import { Alert, Button, Modal, Tabs, Typography, Space, Tag, Collapse, Input, Form, Flex } from "antd"
import { Editor, EditorProvider, Toolbar, BtnBold, BtnItalic, BtnUnderline, createButton } from "react-simple-wysiwyg"
import { useNavigate, useSearchParams, useLocation } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import { oldRoutes } from "router/old-routes"
import { Icon } from "ui/Icon"
import { AlignCenter } from "ui/Icon/DSIcon/AlignCenter"
import { AlignJustify } from "ui/Icon/DSIcon/AlignJustify"
import { useTranslation } from "react-i18next"
import { serialize } from "object-to-formdata"
import { filter } from "lodash"
import pluralize from "pluralize"
import { sendEmail, sendSms, sendMessage } from "store/clients"
import { getCommunicationHistory, COMMUNICATION_HISTORY_PAGE_SIZE } from "store/bookings"
import { hideModal, resetModal, showModal } from "modules/modals/reducer"
import { modalSelector } from "modules/modals/selectors"
import styles from "./ClientsContactWindow.module.css"
import DOMPurify from "dompurify"
import { colors } from "themes/variables"
import { AiEnhancementModal } from "ui/AiEnhancementModal"
import { UnsavedChangesModal } from "ui/UnsavedChangesModal"
import { CLIENT_CONTACT_AI_MAP } from "./constants"

const { TabPane } = Tabs
const { Text, Title } = Typography
const { Panel } = Collapse

const ClientContactForm = ({ handleClose, type, clients, userIds, setSelectedRowKeys, templates, guide, registerRequestClose }) => {
  const location = useLocation()
  const { t } = useTranslation()
  // ...existing code...

  // Use AI_MAP from constants.js

  const bookingUuid = location.state?.bookingUuid || null
  const bookingId = location.state?.bookingId || null
  const lastSegment = location.pathname.split("/").pop()
  const { aiText: aiTextKey = null, aiTooltip: aiTooltipKey = null, aiEnhancer: aiEnhancerKey = null } = CLIENT_CONTACT_AI_MAP[lastSegment] || {}
  const aiText = aiTextKey ? t(aiTextKey) : null
  const aiTooltip = aiTooltipKey ? t(aiTooltipKey) : null
  const aiEnhancer = aiEnhancerKey ? t(aiEnhancerKey) : null
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const EXCLUDED_SHOW_LIMIT = 3

  const { filteredUserIds, excludedClients, warningKey } = useMemo(() => {
    const getExcluded = (predicate) =>
      userIds
        ?.filter((id) => !predicate(clients?.find((c) => c.user_id === id)))
        .map((id) => clients?.find((c) => c.user_id === id))
        .filter(Boolean)

    if (type === "email") {
      const filtered = userIds?.filter((id) => clients?.find((c) => c.user_id === id)?.user?.email)
      return { filteredUserIds: filtered, excludedClients: getExcluded((c) => c?.user?.email), warningKey: "clients.email_warning_filtered" }
    }
    if (type === "text") {
      const filtered = userIds?.filter((id) => clients?.find((c) => c.user_id === id)?.user?.phone_number)
      return { filteredUserIds: filtered, excludedClients: getExcluded((c) => c?.user?.phone_number), warningKey: "clients.phone_warning_filtered" }
    }
    return { filteredUserIds: userIds, excludedClients: [], warningKey: null }
  }, [type, userIds, clients])

  const [showAllExcluded, setShowAllExcluded] = useState(false)

  const [selectedUserIds, setSelectedUserIds] = useState(filteredUserIds)
  const [descriptionValue, setDescriptionValue] = useState("")
  const [profileDescription, setProfileDescription] = useState("")
  const [showAiModal, setShowAiModal] = useState(false)
  const [showUnsavedModal, setShowUnsavedModal] = useState(false)
  const initialUserIdsRef = useRef(userIds)
  useEffect(() => {
    initialUserIdsRef.current = userIds
  }, [])
  const selectedClients = useMemo(() => filter(clients, (client) => selectedUserIds?.includes(client.user_id)), [selectedUserIds, clients])
  const selectedNames = useMemo(
    () => selectedClients.map((item) => `${item?.user?.first_name} ${item?.user?.last_name}`),
    [selectedClients]
  )
  const [messageType, setMessageType] = useState("custom")
  const templateModal = useSelector(modalSelector("templateModal"))
  const { loading } = useSelector((store) => store.clients.sendMessage)
  const [form] = Form.useForm()
  const messageTypeLabels = {
    email: "Email",
    text: "SMS"
  }
  const userType = guide ? "Guides" : "Clients"
  const userSendContact = guide ? "Guides" : "customer"
  const openHandler = () => dispatch(showModal("templateModal"))
  const closeHandler = () => {
    dispatch(hideModal("templateModal"))
    handleClose()
    setSelectedRowKeys([])
  }

  const onClosedHandler = () => {
    dispatch(resetModal("templateModal"))
    if (!bookingId) navigate(oldRoutes.guideClientsPath({}, searchParams))
  }

  const normalizeIds = (ids = []) => ids.map(String).sort((a, b) => a.localeCompare(b)).join(",")
  const getSanitizedText = (value) => {
    const tempDiv = document.createElement("div")
    tempDiv.innerHTML = DOMPurify.sanitize(value || "")
    return tempDiv.textContent?.replaceAll("\u00A0", "").trim() || ""
  }

  const validateBody = async (_, value) => {
    const sanitizedValue = getSanitizedText(value)
    if (!sanitizedValue) {
      throw new Error(t("pro.ai_enhancement.description_too_short"))
    }
    if (type === "text" && sanitizedValue.length > 1000) {
      throw new Error(t("client.error_messages.body_too_long"))
    }
  }

  const hasUnsavedChanges = () => {
    const recipientsChanged = normalizeIds(selectedUserIds) !== normalizeIds(initialUserIdsRef.current)
    const hasSubject = Boolean((form.getFieldValue("subject") || "").trim())
    const hasBody = Boolean(getSanitizedText(form.getFieldValue("body") || descriptionValue))
    const hasTemplate = Boolean(form.getFieldValue("template_id"))
    const tabChanged = messageType !== "custom"

    return recipientsChanged || hasSubject || hasBody || hasTemplate || tabChanged
  }

  const handleRequestClose = () => {
    if (hasUnsavedChanges()) {
      setShowUnsavedModal(true)
      return
    }

    handleClose()
  }

  const handleDiscardChanges = () => {
    setShowUnsavedModal(false)
    handleClose()
  }

  useEffect(() => {
    if (typeof registerRequestClose !== "function") {
      return
    }

    registerRequestClose(handleRequestClose)

    return () => {
      registerRequestClose(null)
    }
  }, [registerRequestClose, selectedUserIds, descriptionValue, messageType, showUnsavedModal])


  const getPayloadKey = (type) => (type === "text" ? "sms" : type)

  const getSendFunction = (type) => {
    const fn = { email: sendEmail, text: sendSms, message: sendMessage }
    return fn[type]
  }

  const buildFormData = (payloadKey, data) => serialize({ [payloadKey]: data })

  const sendPayload = (data) => {
    const payloadKey = getPayloadKey(type)
    const payload = bookingUuid ? { ...data, booking_uuid: bookingUuid } : data
    const formData = buildFormData(payloadKey, payload)
    const sendFn = getSendFunction(type)

    return dispatch(sendFn(formData)).then(() => {
      if (bookingId) dispatch(getCommunicationHistory(bookingId, { page: 1, per_page: COMMUNICATION_HISTORY_PAGE_SIZE }))
      openHandler()
    })
  }

  const handleSubmit = async () => {
    const values = await form.validateFields()
    try {
      const data = {
        client_ids: selectedUserIds,
        body: values.body,
        subject: values.subject,
        type:userType
      }
      await sendPayload(data)
    } catch (error) {
      console.error("Validation failed:", error)
    }
  }

  const onSendTemplate = (templateId) => {
    const data = {
      client_ids: selectedUserIds,
      template_id: templateId
    }
    sendPayload(data)
  }

  const handleRemoveSelected = (name) => {
    // Find the client that has this name
    const clientToRemove = clients.find((client) => `${client.user.first_name} ${client.user.last_name}` === name)
    if (clientToRemove) {
      // Filter out the user_id of the removed client from userIds
      const updatedUserIds = selectedUserIds.filter((id) => id !== clientToRemove.user_id)
      setSelectedUserIds(updatedUserIds)
    }
  }
  const BtnAlignCenter = createButton("Align center", <AlignCenter />, "justifyCenter")
  const BtnAlignJustify = createButton("Align justify", <AlignJustify />, "justifyLeft")
  const messageLabel = messageTypeLabels[type] || "Message"

  const visibleExcluded = showAllExcluded ? excludedClients : excludedClients.slice(0, EXCLUDED_SHOW_LIMIT)
  const hasHiddenExcluded = excludedClients.length > EXCLUDED_SHOW_LIMIT
  const excludedNames = visibleExcluded.map((c) => `${c.user?.first_name} ${c.user?.last_name}`).join(", ")
  const excludedAlertDescription = (
    <span>
      {excludedNames}
      {!showAllExcluded && hasHiddenExcluded && (
        <> <Text strong>+{excludedClients.length - EXCLUDED_SHOW_LIMIT}</Text> <Button type="link" size="small" onClick={() => setShowAllExcluded(true)}>{t("global.show_more")}</Button></>
      )}
      {showAllExcluded && hasHiddenExcluded && (
        <> <Button type="link" size="small" onClick={() => setShowAllExcluded(false)}>{t("global.show_less")}</Button></>
      )}
    </span>
  )
  useEffect(() => {
    form.setFieldsValue({ body: descriptionValue })
  }, [descriptionValue, form])

  return (
    <EditorProvider>
      <Modal
        open={templateModal.isOpen}
        onCancel={closeHandler}
        afterClose={onClosedHandler}
        centered
        
        footer={[
          <div key="footer" style={{ textAlign: "center" }}>
            <Button key="ok" type="primary" onClick={closeHandler}>
              OK
            </Button>
          </div>
        ]} 
        width={"400px"}
      >
        <Title level={4} className="pr-m text-center">
          {`${messageLabel} has been successfully sent to ${pluralize(userSendContact, selectedNames?.length, true)}`}
        </Title>
      </Modal>
      <Form form={form} layout="vertical" onFinish={handleSubmit} >
        <Space direction="vertical" size="large" style={{ width: "100%" }}>
          {excludedClients?.length > 0 && (
            <Alert
              type="warning"
              showIcon
              message={t(warningKey, { count: excludedClients.length })}
              description={excludedAlertDescription}
            />
          )}
          <div>
            <Text style={{ display: "block" }} className="mb-xs">
              {t(`client.${type}`)} {selectedUserIds?.length} {userType}
            </Text>
            <Flex wrap gap={8} className={styles.tagsContainer}>
              {selectedNames?.map((name) => (
                <Tag
                  key={name}
                  {...(selectedNames?.length > 1 ? { closable: true } : {})}
                  onClose={() => handleRemoveSelected(name)}
                  className={styles.groupTag}
                >
                  {name}
                </Tag>
              ))}
            </Flex>
          </div>
          {type !== "message" && (
            <Space>
              <Button
                color={messageType === "custom" ? "primary" : ""}
                variant="filled"
                onClick={() => setMessageType("custom")}
                style={{ borderRadius: "5px" }}
              >
                {t(`client.new_${type}`)}
              </Button>
              {!guide && <Button
                color={messageType === "template" ? "primary" : ""}
                variant="filled"
                onClick={() => setMessageType("template")}
                style={{ borderRadius: "5px" }}
              >
                {t("client.templates")}
              </Button>}
            </Space>
          )}

          <Tabs activeKey={messageType} onChange={setMessageType} className={styles.contactTabs}>
            <TabPane key="custom">
              {type === "email" && (
                <Form.Item
                  name="subject"
                  label={t("client.subject")}
                  rules={[{ required: true, message: t("client.error_messages.subject_required") }]}
                >
                  <Input placeholder="Enter subject" onBlur={(e) => form.setFieldValue("subject", e.target.value.trim())} />
                </Form.Item>
              )}
              <Flex justify="flex-end" className="mb-s">
                <Button
                  disabled={descriptionValue.length < 25}

                  type="primary"
                  icon={<Icon name="aiEnhancement" color={descriptionValue.length < 25 ? colors.textPrimary : colors.disabled} height={20} width={20} />}
                  onClick={() => setShowAiModal(true)}
                >
                  {t("client.enhance_with_ui")}
                </Button>
              </Flex>
              <Form.Item
                name="body"
                rules={[{ validator: validateBody }]}
              >
                <Editor
                  style={{ height: 200, overflow: "auto" }}
                  value={form.getFieldValue("body")}
                  onChange={(e) => {
                    setDescriptionValue(e.target.value)
                  }}
                >
                  <Toolbar>
                    <BtnBold />
                    <BtnItalic />
                    <BtnUnderline />
                    <BtnAlignCenter />
                    <BtnAlignJustify />
                  </Toolbar>
                </Editor>
              </Form.Item>
              <Space className="mt-s">
                <Button key="cancel" color="primary" variant="outlined" onClick={handleRequestClose} size="medium">
                  {t("global.cancel")}
                </Button>
                <Button key="submit" type="primary" htmlType="submit" size="medium" disabled={loading || selectedUserIds?.length === 0}>
                  {t("global.send")}
                </Button>
              </Space>
            </TabPane>

            {type !== "message" && (
              <TabPane key="template">
                {templates?.length > 0 && (
                  <Collapse
                    expandIconPosition="end"
                    defaultActiveKey={form.getFieldValue("template_id")}
                    onChange={(key) => form.setFieldsValue({ template_id: key })}
                  >
                    {templates?.map((template) => (
                      <Panel header={template.name} key={template.id.toString()}>
                        <Space direction="vertical">
                          {template.heading && <Title level={5}>{template.heading}</Title>}
                          {template.category !== "automated" && <Text>Hello [Client Name]</Text>}
                          <div>{template.paragraph_text}</div>
                          {template.button_url && template.template_type !== "email" && <Button type="link">{template.button_url}</Button>}
                          <div>{template.signature}</div>
                          <div>
                            <Text strong italic>
                              [Guide profile name]
                            </Text>
                          </div>
                          <Button type="primary" className="mt-s w-max" onClick={() => onSendTemplate(template.id)}>
                            {t("global.send")}
                          </Button>
                        </Space>
                      </Panel>
                    ))}
                  </Collapse>
                )}
              </TabPane>
            )}
          </Tabs>
        </Space>
      </Form>
      {showAiModal && (
        <AiEnhancementModal
          open={showAiModal}
          description={descriptionValue}
          setDescription={setDescriptionValue}
          profileDescription={profileDescription}
          setProfileDescription={setProfileDescription}
          onOk={() => {
            setShowAiModal(false)
          }}
          onCancel={() => {
            setShowAiModal(false)
            setProfileDescription('')
          }}
          text={aiText}
          modalTitle={aiEnhancer}
          messages={t("pro.ai_enhancement.form.continue_refinement_info")}
          refinementTooltipTitle={t("client.refinement_tooltip")}
          improveWithAITooltipTitle={aiTooltip}
        />
      )}
      <UnsavedChangesModal open={showUnsavedModal} onDiscard={handleDiscardChanges} onKeepEditing={() => setShowUnsavedModal(false)} />
    </EditorProvider>
  )
}

ClientContactForm.propTypes = {
  handleClose: PropTypes.func.isRequired,
  type: PropTypes.oneOf(["email", "text", "message"]).isRequired,
  clients: PropTypes.array.isRequired,
  userIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  setSelectedRowKeys: PropTypes.func,
  templates: PropTypes.arrayOf(PropTypes.object),
  guide: PropTypes.bool,
  registerRequestClose: PropTypes.func
}

export default ClientContactForm
